/**
 * Slider control colours — the canvas half of SliderControls::nav_colors()
 * and ::dots_colors().
 *
 * A fifth control axis alongside placement/nav/dots/reveal. The other four carry
 * variant keys the frontend registry resolves; this one carries values, so it is
 * the only axis the editor can render faithfully on its own — there is no
 * renderer to miss, just custom properties.
 *
 * Only the per-instance tier lives here. theme.json and the
 * `brandy_slider_nav_colors` filter both resolve server-side, so the canvas shows
 * the block's own overrides over the built-in defaults — the same compromise the
 * variant axes make for their "inherit" value.
 *
 * @see inc/Packages/Helpers/SliderControls.php — the same map, same key names.
 */

import { __ } from "@wordpress/i18n";

import { resolvePresetValue } from "./helpers";

/**
 * Canonical key => the `--embla-*` property it drives, mirroring
 * SliderControls::NAV_COLORS. Order is the order the pickers appear in.
 *
 * `--embla-nav-*`, not the `--embla-button-*` the buttons read: the latter are
 * seeded on `.embla` / `.is-editor-preview`, so they are always defined and a
 * nav variant could never fall back to its own colour model. Every arrow rule
 * reads these first and the seeded property second.
 */
export const NAV_COLOR_VARS = {
  icon: "--embla-nav-icon-color",
  iconHover: "--embla-nav-icon-color-hover",
  background: "--embla-nav-bg-color",
  backgroundHover: "--embla-nav-bg-color-hover",
};

/**
 * Picker labels, kept beside the map so a new colour cannot be added to one
 * without the other.
 */
export const NAV_COLOR_LABELS = {
  icon: __("Arrow icon", "brandy-blocks"),
  iconHover: __("Arrow icon (hover)", "brandy-blocks"),
  background: __("Arrow background", "brandy-blocks"),
  backgroundHover: __("Arrow background (hover)", "brandy-blocks"),
};

/**
 * Turn stored nav colours into custom properties for a preview host.
 *
 * The result MUST go on the element carrying `is-editor-preview` (or `.embla`),
 * not on an ancestor: embla.min.css seeds all four properties on that element
 * itself, and an element's own declaration beats anything inherited. Inline on
 * the seeded element is what overrides the seed.
 *
 * Unset keys are omitted rather than defaulted, so a slider the author has not
 * recoloured keeps inheriting the brand default instead of freezing today's
 * value into the canvas.
 *
 * @param {Object} navColors Stored `controlsNavColors` attribute.
 * @return {Object} Style object, empty when nothing is set.
 */
export function navColorVars(navColors) {
  return colorVars(NAV_COLOR_VARS, navColors);
}

/**
 * Canonical key => the `--embla-*` property it drives, mirroring
 * SliderControls::DOTS_COLORS.
 *
 * Three knobs for four dots styles, because the styles are four shapes built
 * from the same three roles. `dotActive` in particular is the active bullet,
 * the fraction's text, the progress pill's fill and the progress bar's fill —
 * one choice keeps them agreeing rather than asking the same question four
 * times in four vocabularies.
 */
export const DOTS_COLOR_VARS = {
  dot: "--embla-dots-color",
  dotActive: "--embla-dots-color-active",
  dotTrack: "--embla-dots-track-color",
};

export const DOTS_COLOR_LABELS = {
  dot: __("Dot", "brandy-blocks"),
  dotActive: __("Dot active / fill", "brandy-blocks"),
  dotTrack: __("Dot track", "brandy-blocks"),
};

/**
 * Turn stored pagination colours into custom properties for a preview host.
 *
 * @param {Object} dotsColors Stored `controlsDotsColors` attribute.
 * @return {Object} Style object, empty when nothing is set.
 */
export function dotsColorVars(dotsColors) {
  return colorVars(DOTS_COLOR_VARS, dotsColors);
}

/**
 * Both axes at once, for the hosts that carry the whole control cluster.
 *
 * @param {Object} attributes Object holding controlsNavColors/controlsDotsColors.
 * @return {Object} Style object, empty when nothing is set.
 */
export function controlColorVars(attributes = {}) {
  return {
    ...navColorVars(attributes.controlsNavColors),
    ...dotsColorVars(attributes.controlsDotsColors),
  };
}

/**
 * Shared applier: map canonical keys onto custom properties, skipping unset.
 *
 * @param {Object} map    Canonical key => custom property.
 * @param {Object} colors Stored attribute object.
 * @return {Object} Style object.
 */
function colorVars(map, colors) {
  if (!colors || typeof colors !== "object") {
    return {};
  }

  return Object.entries(map).reduce((style, [key, property]) => {
    const value = colors[key];

    // A palette pick is stored as `var:preset|color|<slug>`, which is a
    // reference rather than CSS; assigned raw the browser drops the whole
    // declaration and the control silently keeps the default.
    if (value) {
      style[property] = resolvePresetValue(value);
    }

    return style;
  }, {});
}
